/**
 * SKU derivation tier (Phase 65 / BLU-594, tier-1).
 *
 * Reads-only: shows which Shopify option carries colour/size, whether the
 * product type / colour values resolve to codes, and the SKU shape every
 * variant would become. Colour-less products are a SUPPORTED state — the colour
 * segment shows the org's `colourlessCode` with a quiet (not amber) note.
 * Unmapped values render an amber inline chip with a "Map it" jump; an
 * unrecognised option name renders an amber chip (no jump — it is not a mapping
 * fix); duplicate derived SKUs render an amber collision note. Nothing here is
 * written to Shopify — the metafield note makes the "store SKUs are never
 * touched" contract explicit.
 */
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, Info } from 'lucide-react';
import { isCustomSyncProduct, matchSettingsCode, nameListIncludes, type ByoDesignBlocker, type DerivedDesignSet } from '@threadheads/shared';
import { cn } from '@/lib/utils';
import { SkuAnatomy, type AnatomySegment } from './SkuAnatomy';
import { HeldBackNote } from './HeldBackNote';
import { isColourless } from '../lib/status';
import type { CodeTable, PreviewProduct } from '../lib/types';
import type { ProductSyncSettingsBuffer } from '../hooks/useProductSyncSettingsBuffer';

/** Phase 86 slice B: the plan's blocker copy per BYO reason (literal keys, so the i18n check sees them). */
const BYO_BLOCKER_KEY: Record<ByoDesignBlocker, string> = {
  design_id_missing: 'job.blocker.design_id_missing',
  design_id_unreadable: 'job.blocker.design_id_unreadable',
  design_variant_missing: 'job.blocker.design_variant_missing',
  design_variant_unreadable: 'job.blocker.design_variant_unreadable',
};

/** How many colliding SKUs to list inline before collapsing to "and N more". */
const DUP_PREVIEW = 3;

interface SkuDerivationTierProps {
  product: PreviewProduct;
  buffer: ProductSyncSettingsBuffer;
  hasCustomPrintSkuMapping: boolean;
  onMapIt: (table: CodeTable, name?: string) => void;
  /** The row's custom decision (saved settings): hides the Design code row. The SKU keeps its last segment. */
  customProduct?: boolean;
  /**
   * Phase 86 slice B (86-28): a BYO product's light, dark or medium per
   * variant id (the operator's choice, else the metafield's), so the SKU
   * shape shows the letter the run will use. Absent: the metafield's letter.
   */
  byoVariantSets?: Record<string, DerivedDesignSet>;
}

function OkDot() {
  return (
    <span aria-hidden className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-emerald-100">
      <Check className="h-2.5 w-2.5 text-emerald-700" strokeWidth={3} />
    </span>
  );
}

export function SkuDerivationTier({ product, buffer, hasCustomPrintSkuMapping, onMapIt, customProduct = false, byoVariantSets }: SkuDerivationTierProps) {
  const { t } = useTranslation('productSync');
  const { settings } = buffer;

  const typeName = product.productType.name;
  const typeCode = product.productType.code;
  const colourless = isColourless(product);
  const isSingleSize = typeName ? nameListIncludes(settings.singleSizeTypes, typeName) : false;
  // The shared custom rule (type kind, and the per-product switch of a Mixed
  // type), so the anatomy shows exactly what the server derives.
  const isCustomType = isCustomSyncProduct(settings, typeName ?? '', product.id);
  const showSize = !isSingleSize && product.matchedOptions.size !== null;

  const firstOk = product.variants.find((v) => v.derived.ok);
  const colourExample = firstOk?.colourName ? matchSettingsCode(settings.colours, firstOk.colourName) : null;
  const sizeExample = firstOk?.sizeName ? matchSettingsCode(settings.sizes, firstOk.sizeName) : null;
  const darkExample = firstOk?.colourName ? nameListIncludes(settings.darkColours, firstOk.colourName) : false;
  // Phase 86 slice B: a BYO product's letter is the variant's own (L, D or M;
  // the operator's choice first), never darkColours.
  const setLetter = (firstOk ? byoVariantSets?.[firstOk.id] : undefined) ?? firstOk?.byoSet ?? (darkExample ? 'D' : 'L');
  const byoDesign = product.byoDesign;
  // BLU-1522 S8: how many of the org's other own base garments some colours or sizes use.
  const ownGroups = product.ownBlankGroups && !('invalid' in product.ownBlankGroups) ? product.ownBlankGroups.groups.length : 0;

  const segments: AnatomySegment[] = [
    // On-demand-custom types wrap the whole SKU with `CUST-` (matches the
    // backend `pattern` + `deriveVariantSku`); show it as a leading segment.
    ...(isCustomType ? [{ key: 'custom', text: 'CUST', label: t('anatomy.custom') }] : []),
    { key: 'type', text: typeCode ?? '?', label: t('anatomy.type'), tone: typeCode ? 'default' : 'unmapped' },
  ];
  if (showSize) {
    segments.push({ key: 'size', text: sizeExample ?? t('anatomy.size'), label: t('anatomy.size'), tone: 'dynamic' });
  }
  segments.push(
    colourless
      ? { key: 'colour', text: settings.colourlessCode, label: t('anatomy.colour'), tone: 'default' }
      : { key: 'colour', text: colourExample ?? t('anatomy.colour'), label: t('anatomy.colour'), tone: 'dynamic' },
  );
  segments.push({
    key: 'design',
    text: `${product.designCode}${firstOk ? setLetter : ''}`,
    label: isCustomType ? t('anatomy.auto') : t('anatomy.design'),
  });

  const valueGaps = [
    { table: 'colours' as const, names: product.unmappedValues.colours, label: 'tier1.unmappedColour' as const },
    { table: 'sizes' as const, names: product.unmappedValues.sizes, label: 'tier1.unmappedSize' as const },
  ].filter((gap) => gap.names.length > 0);
  const heldBack = product.variantCount - product.derivedCount;

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-8">
      {/* Left: matched-option rows */}
      <div className="flex min-w-[15rem] flex-col gap-2 text-sm">
        {/* Product type */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-24 shrink-0 text-muted-foreground">{t('tier1.productType')}</span>
          {typeCode ? (
            <span className="inline-flex items-center gap-2">
              <OkDot />
              <span>
                {typeName}{' '}
                <span className="font-mono font-semibold text-threadheads-purple">
                  <ArrowRight className="inline h-3 w-3" aria-hidden="true" /> {typeCode}
                </span>
              </span>
              {ownGroups > 0 && (
                <span
                  className="rounded border bg-muted/60 px-1.5 py-0.5 text-[11px] text-muted-foreground"
                  data-testid="own-blanks-count"
                >
                  {t('tier1.moreBaseGarments', { count: ownGroups })}
                </span>
              )}
            </span>
          ) : (
            <span className="inline-flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900">
              {t('tier1.unmappedType', { name: typeName ?? '' })}
              <button
                type="button"
                onClick={() => onMapIt('productTypes', typeName ?? undefined)}
                className="inline-flex h-6 items-center rounded border border-amber-300 px-2 text-[11px] font-semibold text-amber-800 transition-colors duration-200 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none"
              >
                {t('tier1.mapIt')}
              </button>
            </span>
          )}
        </div>

        {/* Colour option — matched, or a quiet colour-less note (supported state) */}
        {product.matchedOptions.colour ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-24 shrink-0 text-muted-foreground">{t('tier1.colourOption')}</span>
            <span className="inline-flex items-center gap-2">
              <OkDot />
              <span>&ldquo;{product.matchedOptions.colour.optionName}&rdquo;</span>
              <span className="rounded border bg-muted/60 px-1.5 py-0.5 text-[11px] text-muted-foreground">
                {t('tier1.matchedColour')}
              </span>
            </span>
          </div>
        ) : colourless ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-24 shrink-0 text-muted-foreground">{t('tier1.colourOption')}</span>
            <span className="text-xs text-muted-foreground">{t('tier1.noColourNote', { code: settings.colourlessCode })}</span>
          </div>
        ) : null}

        {/* Size option */}
        {product.matchedOptions.size && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-24 shrink-0 text-muted-foreground">{t('tier1.sizeOption')}</span>
            <span className="inline-flex items-center gap-2">
              <OkDot />
              <span>&ldquo;{product.matchedOptions.size.optionName}&rdquo;</span>
              <span className="rounded border bg-muted/60 px-1.5 py-0.5 text-[11px] text-muted-foreground">
                {t('tier1.matchedSize')}
              </span>
            </span>
          </div>
        )}

        {/* Design code (a custom product has no design to name) */}
        {!customProduct && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-24 shrink-0 text-muted-foreground">{t('tier1.designCode')}</span>
            {byoDesign && !byoDesign.ok ? (
              // Phase 86 slice B: the product's own design ID or light / dark
              // value can't be used; say why (the plan's own words).
              <HeldBackNote>
                {byoDesign.reason === 'design_variant_unreadable' && byoDesign.badValue
                  ? t('job.blocker.design_variant_unreadable_value', { value: byoDesign.badValue })
                  : t(BYO_BLOCKER_KEY[byoDesign.reason])}
              </HeldBackNote>
            ) : (
              <span className="inline-flex items-center gap-2">
                <OkDot />
                <span className="font-mono font-semibold">{product.designCode}</span>
                <span className="rounded border bg-muted/60 px-1.5 py-0.5 text-[11px] text-muted-foreground">
                  {byoDesign ? t('tier1.fromYourProduct') : t('tier1.fromDesignZone')}
                </span>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Right: anatomy + notes */}
      <div className="min-w-0 flex-1 space-y-2.5">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t('tier1.everyVariant')}</p>
        <SkuAnatomy segments={segments} />

        {/* Unrecognised option — a held-back state, no mapping-table fix */}
        {product.status === 'unrecognised_option' && product.unrecognisedOptionName && (
          <HeldBackNote>{t('tier1.unrecognisedOption', { name: product.unrecognisedOptionName })}</HeldBackNote>
        )}

        {/* Duplicate derived SKUs — collision guard. Cap the list (first N, then
            "and M more") and render each as a mono chip like other code values. */}
        {product.duplicateSkus.length > 0 && (
          <HeldBackNote>
            <span className="align-middle">{t('tier1.duplicateSkusLead')}</span>{' '}
            {product.duplicateSkus.slice(0, DUP_PREVIEW).map((sku) => (
              <span
                key={sku}
                className="mx-0.5 inline-block rounded border border-amber-300 bg-amber-100/70 px-1 py-0.5 align-middle font-mono text-[11px] text-amber-900"
              >
                {sku}
              </span>
            ))}
            {product.duplicateSkus.length > DUP_PREVIEW && (
              <span className="align-middle">{' '}{t('tier1.duplicateSkusMore', { count: product.duplicateSkus.length - DUP_PREVIEW })}</span>
            )}
            <span className="mt-1 block">{t('tier1.duplicateSkusHeldBack')}</span>
          </HeldBackNote>
        )}

        {valueGaps.length > 0 && (
          <div className="space-y-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
            {valueGaps.map((gap) => (
              <div key={gap.table} className="flex flex-wrap items-center gap-2">
                <span>{t(gap.label, { count: gap.names.length })}</span>
                <button
                  type="button"
                  onClick={() => onMapIt(gap.table, gap.names[0])}
                  className="inline-flex h-6 items-center rounded border border-amber-300 px-2 text-[11px] font-semibold text-amber-800 transition-colors duration-200 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none"
                >
                  {t('tier1.mapIt')}
                </button>
              </div>
            ))}
          </div>
        )}

        {heldBack > 0 && product.status !== 'ready' && (
          <p className="text-xs text-muted-foreground">{t('tier1.heldBack', { count: heldBack })}</p>
        )}

        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{t('tier1.metafieldNote', { count: product.derivedCount })}</span>
        </p>

        {!hasCustomPrintSkuMapping && (
          <p className={cn('rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800')}>
            {t('tier1.metafieldMissing')}
          </p>
        )}
      </div>
    </div>
  );
}
